import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSliders } from '@fortawesome/free-solid-svg-icons';

import { CustomButton, Text } from '../../../shared/uiComponents';
import { useScreenWidth } from '../../../shared/Helpers/hooks';
import { Bars } from '../../../shared/Assets/menuIcons';
import { PEARL } from '../../../shared/Helpers/colors';
import AddSession from '../Actions/addSession';
import { months } from '../Helpers/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	setDay,
	setLastDayOfMonth,
	setLastDayOfPreviousMonth,
	setMonth,
	setSelectedDay,
	setWeekDay,
} from '../../../redux/State/clientSlice/calendarHelperSlice';

interface MobileMonthNavigationProps {
	onOpenFilters: () => void;
}

const MobileMonthNavigation: React.FC<MobileMonthNavigationProps> = ({ onOpenFilters }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const shouldMonthNameSlice = screenWidth < 1540;

	const [isExpanded, setIsExpanded] = useState(false);
	const monthScrollContainerRef = useRef<HTMLDivElement>(null);
	const selectedMonthRef = useRef<HTMLDivElement>(null);

	const month = useSelector((state) => state.calendarHelper.month);
	const year = useSelector((state) => state.calendarHelper.year);
	const monthName = shouldMonthNameSlice ? months[month].slice(0, 3) : months[month];

	const toggleExpanded = () => {
		setIsExpanded(!isExpanded);
	};

	const handleMonthSelect = (selectedMonth: number) => {
		dispatch(setMonth(selectedMonth));

		const lastDayOfMonth = new Date(year, selectedMonth + 1, 0).getDate();
		dispatch(setLastDayOfMonth(lastDayOfMonth));

		const lastDayOfLastMonth = new Date(year, selectedMonth, 0).getDate();
		dispatch(setLastDayOfPreviousMonth(lastDayOfLastMonth));

		const nextWeekDay = new Date(year, selectedMonth, 1).getDay();
		dispatch(setWeekDay(nextWeekDay));

		// Set to first day of month when changing month
		dispatch(setDay(1));
		dispatch(setSelectedDay({ day: 1, month: selectedMonth, year, status: 1 }));

		// Don't close navigation when month is selected
	};

	// Auto-scroll to current month when navigation opens
	useEffect(() => {
		if (isExpanded && selectedMonthRef.current && monthScrollContainerRef.current) {
			const container = monthScrollContainerRef.current;
			const selectedElement = selectedMonthRef.current;
			const containerWidth = container.clientWidth;
			const elementLeft = selectedElement.offsetLeft;
			const elementWidth = selectedElement.clientWidth;

			// Center the selected month
			const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;
			container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
		}
	}, [isExpanded, month]);

	return (
		<div className="mobileMonthNavigation">
			<div className="mobileNavHeader">
				<CustomButton iconOnly={<Bars color={PEARL.BASE[600]} />} buttonType="quaternary" className="menuToggle" />

				<div className="dateDisplay" onClick={toggleExpanded}>
					<Text title={`${monthName}, ${year}`} type="h6" fontWeight="semiBold" />
					<FontAwesomeIcon icon={faChevronDown} className="chevronIcon" />
				</div>

				<div className="headerActions">
					<CustomButton
						iconOnly={<FontAwesomeIcon icon={faSliders} />}
						buttonType="secondaryGray"
						onClick={onOpenFilters}
					/>
					<AddSession />
				</div>
			</div>

			{/* Simple expandable month selector - just showing months horizontally */}
			{isExpanded && (
				<div className="expandedNavigation">
					<div className="monthSelector" ref={monthScrollContainerRef}>
						{months.map((monthNameItem, idx) => (
							<div
								key={monthNameItem}
								ref={idx === month ? selectedMonthRef : null}
								className={`monthItem ${idx === month ? 'selected' : ''}`}
								onClick={() => handleMonthSelect(idx)}
							>
								<Text title={monthNameItem.slice(0, 3)} type="psm" fontWeight={idx === month ? 'semiBold' : 'medium'} />
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default MobileMonthNavigation;
